/**
 * Role ids and the read-only rule, in ONE place.
 *
 * These lived only in the SPA (`src/api/types.ts`) while the rule was pure UX gating —
 * appcore refused every write regardless, so a stale copy degraded to "controls shown,
 * appcore 403s" and nothing worse.
 *
 * That stopped being true when the façade needed the same rule as an actual security
 * gate. `POST /assets/{id}/sync-valuation` performs its write on the SYSTEM plane under a
 * service identity, so appcore never sees the caller's roles and its 403 never fires
 * (steward-app#311). The façade's first copy of this list shipped with one of the two
 * read-only roles missing — caught in security review, not by any test, because two
 * hand-maintained copies of a list have no mechanism to disagree loudly.
 *
 * ## Status now: UX gating, plus a temporary deploy-order fallback
 *
 * The façade gate no longer decides from this list. `services/writeGate.ts` asks appcore's
 * capability probe (`GET /w/{ws}/me/capabilities`, crossmarc/appcore#268) and obeys the
 * answer, which appcore derives through the same `evaluateRoleGrant` its own enforced gate
 * uses. `isReadOnlyRoleSet` survives there only as the fallback for environments where
 * appcore has not deployed the route yet — and that fallback is scheduled for deletion
 * (steward-app#436), after which this file is UX gating again and nothing more.
 *
 * Until then a miss on this list still has teeth in one narrow window: an environment
 * where the probe is not yet deployed. Keep it correct; do not grow new consumers.
 */

import { PILLAR_ROLE_IDS, SHARING_PILLARS } from './pillars.js';
import { CASH_FLOW_FAMILY_VIEWER_ROLE_IDS, CASH_FLOW_FIRM_ROLE_IDS, CASH_FLOW_ROLE_IDS } from './cashFlowAccess.js';

/** Role ids from the steward-app App Pack seed (packRoles.ts, written by scripts/seed-steward-app.ts). */
export const STEWARD_ROLE_IDS = {
  ADMIN:   '01HZSTEWRDADMIN000000001',
  MEMBER:  '01HZSTEWRDMEMBR000000001',
  VIEWER:  '01HZSTEWRDVIEWR000000001',
  ADVISOR: '01HZSTEWRDADVSR000000001',
  /**
   * The write-confined contributor role (steward-app#742 step 4). Composed onto an advisor
   * membership ALONGSIDE `ADVISOR` and the pillar roles, only when the family opts in, and
   * only after the firm's ACL entry on the shared folder carries the confinement write verb
   * (`FOLDER_CONTRIBUTE_VERB`, `folderCustody.ts`). Its definition and the whole argument
   * live in `packRoles.ts`; it is listed in {@link NO_WRITE_ROLE_IDS} below, which is the
   * ONE deliberate exception to that list's mechanical rule — read its ⚠️ before touching
   * either.
   */
  ADVISOR_CONTRIBUTOR: '01HZSTEWRDCNTRB000000001',
} as const;

/**
 * Role ids from the **steward-advisor-firm** App Pack seed
 * (`packRoles.ts` `STEWARD_ADVISOR_FIRM_ROLE_DEFS`, written by
 * `scripts/seed-steward-advisor-firm.ts`).
 *
 * A separate table from `STEWARD_ROLE_IDS` above on purpose: that one is documented as
 * the steward-app pack's roles, and two consumers read it as "the ids this app writes
 * onto a membership" (`services/steward-app-api/src/__tests__/aclPrincipalId.test.ts`,
 * and the advisor connect path via `advisorRoleIds()`). **Neither id below is written by
 * `advisorRoleIds()`** — that is still the distinction this separate table exists for.
 *
 * ⚠️ **This paragraph used to end "Nothing writes the id below onto anything — see the role's
 * own docblock in `packRoles.ts` for why nothing *can*", and BOTH halves are now false.**
 * `steward-firm-staff` has been grantable since crossmarc/appcore#501, and steward-app#1343
 * made `steward-firm-authority` `scope: 'workspace'` on 2026-09-09, so a firm administrator
 * grants either through the ordinary member and invite paths — the SPA's own Members page
 * offers both as checkboxes on a firm workspace. What is still true, and is the thing to
 * carry forward, is that no CLIENT-workspace path writes them: a family's workspace mounts
 * `steward-app` only, so neither id resolves there.
 *
 * ⚠️ **Ids must be unique across BOTH packs and appcore's built-ins.** appcore's role
 * cache (`packages/core/src/auth/permissions.ts`) is keyed by `roleId` alone, with no
 * pack segment and a 60s TTL, so a duplicate lets a warm container serve one pack's role
 * to the other pack's workspace. `packRoles.test.ts` asserts the whole union is
 * collision-free.
 */
export const STEWARD_ADVISOR_FIRM_ROLE_IDS = {
  /**
   * The single holder of appcore's `engagement:activate` capability (steward-app#1021,
   * crossmarc/appcore#502).
   *
   * ⚠️ **This comment said "this repo grants it to nobody, and no API can" until 2026-09-09,
   * and steward-app#1343 inverted BOTH halves at once.** The role is `scope: 'workspace'`
   * now, so:
   *
   *  - **this repo DOES grant it** — `src/pages/people/Access.tsx` offers it as a
   *    checkbox on a firm workspace (and, since steward-app#1729, so do the Firm page's People
   *    rows, `src/advisor/YourFirm.tsx`, with the same helper), and the write goes through the
   *    façade's ordinary `PUT /members/{userId}`; and
   *  - **appcore's #504 operator route can no longer grant it.**
   *    `assertElevatedRoleAssignable` refuses an allowlisted id whose role is not
   *    `scope: 'tenant'`, while `assertElevatedRoleRemovable` checks the allowlist alone —
   *    so that route can now only REMOVE. Taking the id out of appcore's
   *    `ASSIGNABLE_ELEVATED_ROLE_IDS` is a separate appcore PR.
   *
   * The id is still exported for the reason it always was: an id an operator or a grant path
   * has to name by hand must not move. Read the role's section in `packRoles.ts` for who may
   * assign it and what bounds it.
   */
  FIRM_AUTHORITY: '01HZSTEWRDFRMAU000000001',
  /**
   * The single holder of appcore's `engagement:read` capability (crossmarc/appcore#501).
   *
   * `scope: 'workspace'`, so it IS assignable through the ordinary member and invite
   * surfaces. That used to be stated as "unlike `FIRM_AUTHORITY` … that asymmetry is the
   * point rather than an oversight"; steward-app#1343 gave both roles the same scope on
   * 2026-09-09, so there is no asymmetry left to be the point. What still differs is BLAST
   * RADIUS, not assignability: this one shows a firm its own client list and confers no
   * write of any kind, while firm authority binds a household to the firm. See both roles'
   * sections in `packRoles.ts`.
   */
  FIRM_STAFF: '01HZSTEWRDFRMST000000001',
  /**
   * The single holder of appcore's `firmroster:write` capability (crossmarc/appcore#645): a
   * firm admin maintains the firm's own advisor roster from the "Your firm" page.
   *
   * `scope: 'workspace'`, the same as the two roles above, so a firm admin grants it through
   * the ordinary member and invite paths. NOT every path that provisions a firm admin grants
   * it: "Your firm"'s Add a person does (choosing Admin ticks it beside `FIRM_AUTHORITY`), but
   * Settings -> Members invites an Admin with the one role id it picks, the backfill converges
   * only the admins present when it runs, and appcore's `seed-advisor-firm` does not bind it.
   * Those are listed in `packRoles.ts` ("Who gets it by default"). It confers no `inst:write` and no engagement capability;
   * what it does write is the firm's public roster (who is listed, their name and title),
   * which is why it is a separate role rather than a permission folded into either of the
   * others. See its section in `packRoles.ts`.
   */
  FIRM_ROSTER: '01HZSTEWRDFRMRS000000001',
  /**
   * The single holder of appcore's `engagement:provision` capability (steward-app#1481, SCS
   * 1.4): setting up a sponsored Steward account for a client from the firm's side
   * (crossmarc/appcore#570 / #688).
   *
   * `scope: 'workspace'`, like the three above, so a firm admin grants and revokes it through
   * the ordinary member and invite paths. Founder decision 3 on steward-app#1016 (2026-09-16):
   * "any firm admin or advisor" may set up an account, implemented as its OWN role so that
   * narrowing it later is a role change on a membership rather than a code change. So every
   * INVITE path in this repo that makes someone a firm Admin or Member (never a Viewer, see
   * {@link mayHoldFirmProvision}) composes it beside `FIRM_STAFF`
   * (Settings → Members' per-role checkboxes do not: ticking Firm staff grants only firm staff;
   * see `packRoles.ts`, "Who gets it by default"). The name census is
   * `scripts/lib/firmProvisionGrantSites.test.ts`. It confers no `inst:write`
   * and is never held by a steward-app (family) pack role. See its section in `packRoles.ts`.
   */
  FIRM_PROVISION: '01HZSTEWRDFRMPV000000001',
} as const;

/**
 * appcore platform roles that grant reads only. Keep in sync with appcore
 * `packages/core/src/entities/role.ts` (`SEEDED_ROLES`).
 *
 * `workspace-viewer` holds `*:read` and no `*:write`, and its scope is `workspace`, so any
 * `mem:manage` holder can assign it through the ordinary member and invite surface. It is
 * assignable in practice, not theoretical.
 */
/**
 * ⚠️ **EVERY PACK-GLOBAL ADVISOR ROLE ID: the ids an advisor of ANY firm can hold**
 * (steward-app#742 step 4, added after that PR's security review found the list was being
 * kept by hand in one file and had already fallen behind).
 *
 * This is the input to one question, asked in two places in
 * `services/steward-app-api/src/handlers/advisor.ts`: when harvesting the FAMILY's role ids
 * off the member roster to write a folder ACL, which ids must be stripped? Every id here is
 * shared by every firm, so writing one as a `ROLE` entry on a folder grants that folder to
 * every advisor of every connected firm, permanently. That is steward-app#732's cross-firm
 * leak, and steward-app#787 is the same bug arriving through a role added later.
 *
 * ⚠️ **A role added to the advisor set MUST be added here, and nothing enforces that but
 * this docblock and `packRoles.test.ts`.** The review that found it put the case plainly: a
 * predicate whose documented job is "an id every advisor holds" was left behind by a PR
 * that added exactly such an id. It lives in this package rather than in the handler
 * precisely so the definition sits next to `STEWARD_ROLE_IDS`, where a new advisor role is
 * declared.
 *
 * ⚠️ **NOT the same list as `advisorRoleIds()` in the façade**, which is what `connect`
 * MINTS onto an invitation: that one is the base advisor role plus the chosen pillars, and
 * deliberately never the contributor (which the shared-folder reconcile composes later, in
 * a fixed order relative to the ACL). This list is what must be STRIPPED, so it is the
 * union of everything an advisor membership can end up carrying.
 */
export const ADVISOR_SCOPE_ROLE_IDS: readonly string[] = [
  STEWARD_ROLE_IDS.ADVISOR,
  STEWARD_ROLE_IDS.ADVISOR_CONTRIBUTOR,
  ...PILLAR_ROLE_IDS,
  // steward-app#2581: the two cash flow FIRM roles. Composable onto an advisor membership
  // like a pillar role, but NOT pillars (`cashFlowAccess.ts` says why), so `PILLAR_ROLE_IDS`
  // does not carry them here and they are listed by hand.
  //
  // ⚠️ The two cash flow FAMILY viewer roles are deliberately NOT here. This list also decides
  // who IS an advisor (`advisorUserIds` and the invite-accept refusal in the façade,
  // `canBeAdvisor` in `sponsoredFloor.ts`), so listing them would class every family member
  // holding one as an advisor (docs/39 §8.5). They are kept off folder ACLs by a separate strip
  // in `familyRoleIds` instead. `cashFlowAccess.test.ts` pins both directions.
  ...CASH_FLOW_FIRM_ROLE_IDS,
];

/** True when this id is one every advisor of every firm can hold, and so must never be
 *  written onto a folder ACL as a FAMILY principal ({@link ADVISOR_SCOPE_ROLE_IDS}). */
export function isAdvisorScopeRoleId(roleId: string): boolean {
  return ADVISOR_SCOPE_ROLE_IDS.includes(roleId);
}

export const PLATFORM_READ_ONLY_ROLE_IDS = {
  WORKSPACE_VIEWER: '01HXRR0LE00WSVIEWER0000001',
} as const;

/**
 * appcore platform roles that grant `inst:write` WITHOUT granting `mem:manage`
 * (steward-app#742 step 4, second security review).
 *
 * ⚠️ This said "without granting break-glass" until steward-app#1254. The membership test
 * below is unchanged — it is still the literal `mem:manage` these two roles lack — but the
 * word had come to mean "clears every gate", and appcore#603 retired that meaning: the set
 * that short-circuits a capability check is `['*:*']` now. Say the string, not the label.
 *
 * Verified at source in crossmarc/appcore `packages/core/src/entities/role.ts` (2026-09-02),
 * because every property below is load-bearing and none of them is guessable from the name:
 *
 *   - `workspace-operator` (`01HXRR0LE00QR5F9KA2B7CY3NW`): `scope: 'workspace'`; permissions
 *     include `inst:read`, `inst:write`, `credential:read`, `mem:read` — and **not**
 *     `mem:manage`, `mem:*` or `*:*`;
 *   - `workspace-editor` (`01HXRR0LE00WSEDITOR0000001`): the same, one capability narrower.
 *
 * `scope: 'workspace'` is what makes them REAL rather than theoretical: `assertAssignableRoles`
 * admits them on every member and invite surface, so a family can grant one to their own
 * people today, and `folderVisibility.test.ts` has carried a `workspace-editor` member fixture
 * since before this change.
 *
 * ⚠️ **The absence of `mem:manage` is exactly why they need naming**, and the reason is the
 * second sentence below rather than the first. This used to lead with "a `mem:manage` holder
 * clears appcore's gates above write confinement, so confining them changes nothing", which
 * is now wrong twice over: appcore#603 stopped `mem:manage` clearing a capability check at
 * all, and confinement was never one of the gates it cleared — appcore suppresses the
 * Tier-3 ACL-administrator exemption inside the confined pass, so confinement wins, and
 * `assertConfinedRoleNotAdmin` refuses a role that carries both anyway.
 *
 * The surviving reason needs no comparison to an admin: these two are the shape that gets
 * hurt — an ordinary writer, in the family's own workspace, whose membership a fail-closed
 * confinement union would silence.
 *
 * ⚠️ **SECOND CONSUMER, and it WRITES these ids rather than reading them** (steward-app#835).
 * `familyRoleIds` (`services/steward-app-api/src/handlers/advisor.ts`) seeds both
 * unconditionally into the family ROLE principals it puts on the advisor shared folder and on
 * every "Only your family" folder, so a member invited after a folder was restricted is not
 * locked out of it by a point-in-time harvest. Two things follow that a future editor has to
 * know before touching this object:
 *
 *   - **an id added here is now an ACL entry every such folder acquires**, so it must be an id
 *     no advisor of any firm can hold except by the family's own deliberate grant. Both of
 *     these qualify (`scope: 'workspace'`, granted only through the member and invite
 *     surfaces, and listed in {@link FAMILY_ROLE_IDS} for that reason). A pack-global
 *     advisor-held id must never be added — that is steward-app#732's cross-firm leak;
 *   - **it is deliberately NOT the same set as that file's `SEED_FINGERPRINT_ROLE_IDS`**,
 *     which stays at the three steward ids. Read that constant's ⚠️ before assuming the two
 *     should be kept in step: widening the fingerprint reclassifies every folder an earlier
 *     deploy wrote as hand-edited.
 */
export const PLATFORM_WRITER_ROLE_IDS = {
  WORKSPACE_OPERATOR: '01HXRR0LE00QR5F9KA2B7CY3NW',
  WORKSPACE_EDITOR:   '01HXRR0LE00WSEDITOR0000001',
} as const;

/**
 * Every role id known to confer **no `inst:write`**.
 *
 * ⚠️ **That, and not "read-only", is the predicate** (steward-app#1102). This list exists
 * to approximate exactly ONE question that `services/writeGate.ts` otherwise puts to
 * appcore's capability probe: does this membership hold `WRITE_CAPABILITY`, which that file
 * pins to the literal `inst:write` (`writeGate.ts`)? So the test for an entry is mechanical:
 * take the role's declared permission strings and check that none of them reaches
 * `inst:write` under appcore's `matchCapability` expansion, which is exact match, `*:*`, and
 * `<prefix>:*` and nothing else. A role may carry a genuine write verb of something in
 * ANOTHER system and still belong here; what it may not carry is `inst:write`.
 *
 * The constant was called `READ_ONLY_ROLE_IDS` until #1102. It was renamed because prose in
 * this repo had started reasoning from membership of a list named "read only" to real-world
 * safety claims — `services/steward-app-api/src/handlers/folderVisibility.ts` argued from it
 * that an advisor "cannot move anything", and that comment was rewritten in the same change
 * to argue from the roles' own capability strings instead — while one entry below carries a
 * state change that is emphatically not a read. {@link isReadOnlyRoleSet} keeps its older name for now:
 * the constant is the thing quoted in prose, the predicate is not, and renaming both at once
 * would make the constant's rename hard to revert on its own.
 *
 * `STEWARD_ROLE_IDS.ADVISOR` (steward-app#643) MUST be here: `advisorRoleIds()` in
 * `services/steward-app-api/src/handlers/advisor.ts` pins every advisor connection to
 * this role, and missing it here is the most likely way to get that feature wrong — an
 * advisor whose membership fails `isReadOnlyRoleSet` renders as a writer in the SPA
 * (`useCanWrite`) even though appcore still refuses every write appcore-side.
 *
 * `PILLAR_ROLE_IDS` (steward-app#739) are here for the SAME reason. They were added in
 * phase 1, ahead of anybody holding one; since phase 2a the façade's `connect` composes
 * the default areas onto every new advisor invitation and `PUT /advisor/shared-areas`
 * composes a family's choice onto existing memberships, so real memberships now read
 * `[ADVISOR, portfolio-share, …]` and this list is load-bearing rather than anticipatory.
 * `isReadOnlyRoleSet` requires EVERY id to be recognised, so an unlisted pillar role
 * would flip exactly the advisor memberships this feature creates from read-only to
 * "writer" in the SPA. Each holds a single `<pillar>:read` string and no write verb of
 * any kind (asserted in `pillars.test.ts`), so listing them is a statement of fact, not
 * an assumption. Adding them now is inert and keeps the two halves from being separated
 * by weeks.
 *
 * `STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_AUTHORITY` (steward-app#1102) is here on the
 * `inst:write` argument above and NOT on the pillar roles' argument, which does not
 * generalise to it: `engagement:activate` is a write verb, of a row in appcore's engagement
 * store. Its permission set is exactly `['engagement:activate']` (`packRoles.ts`, pinned by
 * `packRoles.test.ts`), and none of `matchCapability`'s three expansions carries
 * `engagement:activate` to `inst:write` (pinned by `packRoles.test.ts` too, so this
 * paragraph is a property and not a comment). Omitting it was the #1102 bug and it was NOT
 * inert: `isReadOnlyRoleSet` requires every id to be recognised, so a membership carrying
 * this id alongside otherwise-recognised roles read as a WRITER — the fallback disagreeing
 * with the probe it exists to approximate, on the only shapes where they disagreed at all.
 *
 * ⚠️ The shape that made it durable is a MIS-landed grant, not the intended one. `packRoles.ts`
 * instructs the out-of-band grant (crossmarc/appcore#504) onto a membership in the FIRM'S OWN
 * tenant, never onto a client workspace's; do not read the `[ADVISOR, <pillar>, …]` case in
 * `packRoles.test.ts` as the composition anybody is told to produce, because `advisorRoleIds()`
 * mints that array only for a CLIENT workspace. It is the case that matters anyway: `PUT
 * /advisor/shared-areas` carries a member's non-pillar roles through verbatim and skips the
 * write when the set is unchanged (`handlers/advisor.ts`), so a stray grant there sits reading
 * as "writer" indefinitely rather than being corrected by the next role write.
 *
 * `STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_STAFF` (crossmarc/appcore#501) is here on the same
 * mechanical test, and its case is the easy one: its permission set is exactly
 * `['engagement:read', 'onboarding:request']` (`packRoles.ts`, pinned by `packRoles.test.ts`;
 * the second string since steward-app#1426), a read of appcore's engagement store plus the
 * firm-side capability of the engagement-authorized write, which appcore resolves only from
 * firm-tenant memberships and which, today, writes one `onboarding-request` row through its own
 * route (the only type declaring it; a second declaring type would widen what this role reaches).
 * None of `matchCapability`'s three expansions carries either string to `inst:write`. Unlike `FIRM_AUTHORITY` this role is
 * `scope: 'workspace'` and therefore genuinely assignable through the member and invite
 * paths, so real memberships WILL carry it — omitting it would not be latent. A firm-staff
 * membership is `[ADMIN, FIRM_STAFF]` in practice, which `isReadOnlyRoleSet` correctly reads
 * as false because `ADMIN` is not on this list; the entry matters for the shapes where every
 * other id IS listed (a viewer or advisor membership that also carries firm staff).
 *
 * Listing it cannot loosen anything: every live consumer of this list (`writeGate`,
 * `assistant/callerRole`, the SPA's `isReadOnlyMember`) treats a `true` as "deny or hide",
 * never as a grant.
 *
 * `STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_ROSTER` (crossmarc/appcore#645) is here on the same
 * mechanical test as `FIRM_AUTHORITY`: its permission set is exactly `['firmroster:write']`
 * (`packRoles.ts`, pinned by `packRoles.test.ts`), a write of the firm's own advisor roster
 * record in appcore and nothing else, and none of `matchCapability`'s three expansions carries
 * `firmroster:write` to `inst:write` (pinned by `packRoles.test.ts`). It rides beside
 * `FIRM_AUTHORITY` on a firm admin's membership, where `ADMIN` or `WORKSPACE_ADMIN` already
 * makes `isReadOnlyRoleSet` false; the entry matters for a staff member who is given the roster
 * without being made an admin, whose membership would otherwise read as a WRITER.
 *
 * `STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_PROVISION` (steward-app#1481) is here on the same
 * mechanical test: its permission set is exactly `['engagement:provision']` (`packRoles.ts`,
 * pinned by `packRoles.test.ts`), and none of `matchCapability`'s three expansions carries it to
 * `inst:write` (pinned there too). What it DOES let appcore do on the holder's behalf is create
 * a client's tenant and workspace through appcore's sponsored-setup route; that is a write in
 * appcore's control plane under appcore's own gate, not an `inst:write` in the workspace this
 * list is consulted about. By default it rides beside `FIRM_STAFF` on a firm person's membership
 * (not always: a firm admin can untick it, or tick Firm staff alone), so the entry matters for
 * the shapes `FIRM_STAFF`'s paragraph names. A Viewer set carrying it is refused at the façade
 * ({@link firmProvisionWithoutBaseRole}), but one written by another plane is still classified
 * read-only here, which is the fail-closed direction.
 *
 * ⚠️⚠️ **`STEWARD_ROLE_IDS.ADVISOR_CONTRIBUTOR` IS THE ONE DELIBERATE EXCEPTION TO THE
 * MECHANICAL RULE ABOVE, AND IT IS A PRODUCT DECISION (steward-app#742 step 6).** That role
 * holds `inst:write` outright (`packRoles.ts`), so the mechanical test says it does not
 * belong here. It is listed anyway, and the reason is what this list is actually FOR:
 *
 *   - **Every live consumer treats `true` as "deny or hide", never as a grant** — the
 *     paragraph below already says so for `FIRM_STAFF`, and it is what makes listing a
 *     writer safe rather than merely convenient. Listing a role can only ever withhold.
 *   - **What that role can write is one folder's contents, and `useCanWrite` is a
 *     WORKSPACE-WIDE switch.** Left off this list, `isReadOnlyRoleSet` would read an
 *     advisor membership as a writer and the SPA would light up every create, edit and
 *     delete control in the client's portal — new assets, new contacts, delete a valuation
 *     — for a principal appcore refuses on all of them (every non-document type declares
 *     `writeCapability: 'steward:write'`, which this role does not hold, or, for
 *     `onboarding-request`, a string no role holds). That is a portal
 *     full of buttons that 403, on the page a family opens to see who can touch their
 *     things.
 *   - **The narrow affordance is gated NARROWLY**, by `src/lib/useCanContributeToSharedFolder.ts`,
 *     which asks for this role id AND that the folder in view is the firm's own shared
 *     folder. So the contributor's real permission is offered exactly where it exists,
 *     instead of being approximated by a switch that means something wider.
 *
 * ⚠️ **Checked before it was decided: no server-side consumer refuses the contributor's
 * DOCUMENT write because of this entry.** The three that gate anything are
 * `services/writeGate.ts` (its fallback path only — and it guards the SYSTEM-PLANE routes
 * of #311/#314, valuation sync, connection sync, re-link, AVM refresh; the contributor must
 * be refused there and now is, on the probe path too, since that gate asks for
 * `steward:write` as well as `inst:write`), `services/realEstateRefresh.ts` (the same
 * system-plane refresh), and `assistant/callerRole.ts` (which maps it to `viewer`, the
 * honest answer for a principal who can write no portfolio row). **No document route
 * consults this list at all**: a document create/update travels on the caller's own token
 * to appcore, where write confinement and the folder ACL decide it. Verified by grep over
 * `services/steward-app-api/src` and `src`, 2026-09-02.
 *
 * The consequence to keep true: if a future consumer ever reads a `true` here as "this
 * principal writes nothing anywhere", that consumer is wrong about this role, exactly as the
 * `FIRM_AUTHORITY` paragraph above says it would be wrong about that one. The predicate is
 * "holds no capability this SPA gates a workspace-wide write affordance on", and the name
 * `isReadOnlyRoleSet` has been outrunning its meaning since #1102.
 */
export const NO_WRITE_ROLE_IDS: readonly string[] = [
  STEWARD_ROLE_IDS.VIEWER,
  STEWARD_ROLE_IDS.ADVISOR,
  // See the ⚠️⚠️ above: a genuine `inst:write` holder, listed deliberately.
  STEWARD_ROLE_IDS.ADVISOR_CONTRIBUTOR,
  STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_AUTHORITY,
  STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_STAFF,
  STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_ROSTER,
  STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_PROVISION,
  PLATFORM_READ_ONLY_ROLE_IDS.WORKSPACE_VIEWER,
  ...PILLAR_ROLE_IDS,
  // steward-app#2581: all four cash flow roles hold a cash flow READ and no write verb.
  // `isReadOnlyRoleSet` is an `every`, so an unlisted id would make `steward-viewer` plus a
  // viewer role read as a WRITER (docs/39 §8.5). `cashFlowAccess.test.ts` pins it.
  ...CASH_FLOW_FAMILY_VIEWER_ROLE_IDS,
  ...CASH_FLOW_FIRM_ROLE_IDS,
];

/**
 * appcore platform seeded admin roles. A workspace created through the platform
 * "add workspace" / founder-bootstrap flow grants its creator the platform
 * WORKSPACE_ADMIN (and, for bootstrap, TENANT_ADMIN) role — NOT the steward-app custom
 * `ADMIN` role, which is only assigned by steward-app's own seed/invite path. Any
 * owner-gated check must recognize both families, or a workspace's own creator would be
 * refused in their own workspace.
 */
export const PLATFORM_ADMIN_ROLE_IDS = {
  TENANT_ADMIN:    '01HXRR0LE00ORGADM3N1X4Y5Z8',
  WORKSPACE_ADMIN: '01HXRR0LE00WSADMIN00000001',
} as const;

/**
 * Role ids appcore will **refuse to ADD to, or REMOVE from, a membership** through the
 * ordinary member and invite surface (crossmarc/steward-app#1087, found in that PR's
 * security review).
 *
 * ⚠️ **CARRYING one through is no longer a refusal (crossmarc/appcore#555, merged as PR #557 on 2026-09-02),
 * and this docblock said the opposite until then.** It used to read "refuse to write BACK
 * onto a membership, so a caller that re-sends a member's whole `roleIds` array cannot
 * carry them through", which was true of the rule as first written and is now false.
 * `assertAssignableRoles` takes the membership's CURRENT roles as its second argument and
 * applies the `scope: 'workspace'` rule to the DELTA: every ADDED id must be
 * workspace-scoped, every REMOVED id must be workspace-scoped, and a CARRIED id "passes
 * regardless of scope, and is not read at all" (verified at source in crossmarc/appcore
 * `packages/core/src/auth/permissions.ts`, 2026-09-02). So a whole-array `updateMember`
 * that restates what the membership already holds and appends only workspace-scoped ids
 * SUCCEEDS — which is what let `handlers/advisor.ts` drop its founder carve-out.
 *
 * ⚠️ **This is still a property of appcore, not a policy of ours, and it is still
 * fail-closed on both edges.** A caller may not ADD a role appcore cannot resolve as
 * `scope: 'workspace'` in the target workspace, and may not STRIP one either: that is
 * appcore#358's security property, and it is what makes such a role unforgeable from the
 * member plane. Nothing here relaxes it; this list only lets a caller SEE the refusal coming
 * instead of discovering it as an unexplained 403 half way through a write.
 *
 * ⚠️ **So a consumer must ask which EDGE its write is on, and that is the whole use of
 * this list now.** `handlers/advisor.ts`'s pin loop is the worked example and it needs the
 * list for one of its two branches and not the other: the admin branch writes
 * `existing ∪ advisorRoleIds()`, carries everything, and does not consult this list; the
 * other branch writes a whole-array REPLACE, which asks appcore to REMOVE every id the
 * membership held, and appcore refuses that removal — a `403 ROLE_NOT_ASSIGNABLE` that fails
 * the caller's whole request. A consumer that reads this list as "appcore will not write
 * these back, full stop" will now be wrong in one direction or the other.
 *
 * ⚠️ **That sentence used to say "a tenant-scoped one there", and the pin loop's only real
 * case is no longer tenant-scoped.** Since steward-app#1343 the two entries are refused for
 * two DIFFERENT reasons, spelled out in the list's own docblock below: `TENANT_ADMIN` by its
 * scope, `FIRM_AUTHORITY` because it does not resolve in a client workspace at all. The
 * refusal is the same 403 either way; the mechanism is not, and only one of them is reliable
 * on a cold Lambda.
 *
 * ⚠️ **THE TWO ENTRIES ARE HERE FOR DIFFERENT REASONS, and one of them changed on
 * 2026-09-09.** This section used to open "Both entries are `scope: 'tenant'`", which is no
 * longer true and was never the whole reason for the second one.
 *
 *   - `PLATFORM_ADMIN_ROLE_IDS.TENANT_ADMIN` — **scope.** `scope: 'tenant'`, and emphatically
 *     not exotic: appcore's `OnboardingRepository` mints every self-signup founder's
 *     membership as `[TENANT_ADMIN, WORKSPACE_ADMIN]`, so any workspace whose creator is also
 *     the address a caller is composing onto carries it.
 *   - `STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_AUTHORITY` — **NOT scope any more; it is
 *     `scope: 'workspace'` since steward-app#1343. It is here because it does not RESOLVE
 *     where this list is consulted.** The consumers are client-workspace writers, and a client
 *     workspace mounts `steward-app` only, so appcore's `loadRole` finds no pack facet for
 *     this id there and `assertAssignableRoles` refuses it on both edges — the same 403, from
 *     the mount rather than from the scope.
 *
 *     ⚠️ **KEEP IT IN THIS LIST.** The refusal it now rests on is reliable COLD and a coin
 *     flip WARM: appcore's role cache is keyed by roleId alone and consults the workspace's
 *     packs only on a miss (60s TTL, `auth/permissions.ts`), so an id resolved for a firm
 *     workspace in the last minute resolves in a family's workspace too. Dropping the entry
 *     would hand `handlers/advisor.ts`'s connect pin loop a whole-array REPLACE whose success
 *     depends on Lambda warmth — a 403 that fails a family's connect permanently on some
 *     retries and not others. Skipping such a membership and saying so is the honest handling.
 *
 * ⚠️ **A membership carrying one of these can be READ and CARRIED, and demoted only by a
 * write that omits it — which appcore refuses, on the REMOVED edge.** So the honest handling
 * for a whole-array writer is to restate the id and compose beside it, never to "helpfully"
 * drop it: dropping it asks appcore to REMOVE a role that does not resolve there (or, for
 * `TENANT_ADMIN`, one whose scope is wrong), and appcore answers `403 ROLE_NOT_ASSIGNABLE`
 * rather than doing it.
 */
export const UNASSIGNABLE_ROLE_IDS: readonly string[] = [
  PLATFORM_ADMIN_ROLE_IDS.TENANT_ADMIN,
  STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_AUTHORITY,
];

/** True when the membership carries a role {@link UNASSIGNABLE_ROLE_IDS} names, i.e. one a
 *  member-surface write may neither grant nor strip. It MAY be carried through since
 *  crossmarc/appcore#555 — read that list's ⚠️ before using this as a skip. */
export function hasUnassignableRole(roleIds: readonly string[]): boolean {
  return roleIds.some(id => UNASSIGNABLE_ROLE_IDS.includes(id));
}

/**
 * The steward and platform roles a FAMILY grants one of its own people, as distinct from
 * the ones `advisorRoleIds()` mints for a connected firm (crossmarc/steward-app#1087).
 *
 * ⚠️ **What this list is for: an honesty check, not an authorization one.** Settings →
 * Advisors tells a family that a connected firm is "read-only … and cannot see anything in
 * your credential vault". That is true of a membership holding only the advisor role set,
 * and false of one that ALSO holds a family role — `steward-viewer` carries
 * `credential:read` and `steward-member` carries `credential:reveal` and `inst:write`
 * (`packRoles.ts`), so such a person reads the vault whatever the shared-areas switches
 * say. The screen has to qualify the claim, and this is the predicate that says when.
 *
 * An unrecognised role id yields FALSE, and that direction is chosen rather than inherited:
 * every id this product grants a family member is listed, so an unknown one is not a family
 * role, and a caveat drawn over an id nobody can explain would be a claim about somebody's
 * access made on no evidence.
 */
export const FAMILY_ROLE_IDS: readonly string[] = [
  STEWARD_ROLE_IDS.ADMIN,
  STEWARD_ROLE_IDS.MEMBER,
  STEWARD_ROLE_IDS.VIEWER,
  PLATFORM_ADMIN_ROLE_IDS.WORKSPACE_ADMIN,
  PLATFORM_ADMIN_ROLE_IDS.TENANT_ADMIN,
  PLATFORM_READ_ONLY_ROLE_IDS.WORKSPACE_VIEWER,
  // ⚠️ **The two appcore WRITER roles, added by steward-app#742 step 4's second security
  // review, and their absence was not cosmetic.** This list was assembled while it had one
  // consumer — an honesty caveat on a screen — where a missing id costs a sentence. #742 gave
  // it a second consumer that DECIDES something: `handlers/advisor.ts` refuses to compose the
  // write-confined contributor role onto a membership this predicate matches, because
  // appcore resolves confinement as a fail-closed UNION over the membership's roles. A
  // membership of `[steward-advisor, <pillar>, workspace-editor]` was therefore not
  // recognised as a family member, received the confined role, and had its WHOLE workspace
  // confined: that person could no longer write an asset or a contact of their own, only
  // documents inside a folder whose ACL names them at the write verb.
  //
  // Reachable rather than theoretical, and by the ordinary route: both roles are
  // `scope: 'workspace'`, so a family admin can grant either through Settings → Members or an
  // invite, and this file's own `NO_WRITE_ROLE_IDS` docblock already records that the
  // production workspace's writing members hold appcore platform roles and no steward role at
  // all. See `PLATFORM_WRITER_ROLE_IDS` for the source verification.
  PLATFORM_WRITER_ROLE_IDS.WORKSPACE_OPERATOR,
  PLATFORM_WRITER_ROLE_IDS.WORKSPACE_EDITOR,
];

/** True when the membership carries a role the FAMILY granted in their own right
 *  ({@link FAMILY_ROLE_IDS}), beside whatever the advisor connection put there. */
export function hasFamilyRole(roleIds: readonly string[]): boolean {
  return roleIds.some(id => FAMILY_ROLE_IDS.includes(id));
}

/**
 * `roleIds` with every READ-CONFINING role (a pillar's `offRoleIds`, steward-app#1950) removed
 * where it would CONTRADICT another role on the same membership. The array to write, in the
 * caller's order; a fresh one every call.
 *
 * appcore resolves read confinement as a UNION over the membership's roles
 * (crossmarc/appcore#786), so a confining role is never cancelled by a wider one beside it:
 * it narrows the whole membership. Two compositions therefore have to be prevented, not
 * merely tolerated:
 *
 *   1. **Beside a FAMILY role** ({@link hasFamilyRole}). The Documents-off role on a person the
 *      family ALSO made a Viewer, Member or Admin would confine that person's reading of the
 *      whole vault to the one shared folder. Dropping it leaves exactly what the membership's
 *      other roles grant, unconfined: documents if a family role carries `documents:read`,
 *      none if it does not. That is the access the family chose to give the person. The same
 *      reason the façade never composes the write-confined contributor onto such a membership.
 *   2. **Beside its own pillar's PRIMARY role.** Area ON and area OFF at once. `connect`'s
 *      admin-held branch writes `existing ∪ advisorRoleIds()`, so a union can produce it
 *      whenever the areas moved; left alone, the firm would be confined while the family's
 *      screen says the area is shared.
 *
 * Applied by the four façade writes that set a membership's role set from areas or from the
 * caller: `setSharedAreas`, `connect`'s pin, `POST /members` and `PUT /members/{userId}` (where
 * a family admin would add a family role to a connected advisor). Invites do not need it:
 * appcore refuses an invite for someone who is already an active member, so an invite's roles
 * never union onto an existing membership. Each of the four is pinned by a test that drops
 * the role beside a family role.
 */
export function withoutConflictingConfinement(roleIds: readonly string[]): string[] {
  const held = new Set(roleIds);
  const family = hasFamilyRole(roleIds);
  const drop = new Set(SHARING_PILLARS.flatMap(p => (family || held.has(p.roleId) ? p.offRoleIds : [])));
  return roleIds.filter(id => !drop.has(id));
}

/**
 * True when the membership carries any admin-capable role: steward-app's own `ADMIN` or
 * either appcore platform admin.
 *
 * Unlike {@link isReadOnlyRoleSet} this **fails closed** — an unrecognised role is not
 * treated as admin. The asymmetry is deliberate and follows from what each rule protects.
 * A read-only miss would refuse a legitimate *writer*, and production proves that case is
 * real (the family workspace's writing members hold platform roles only), so that rule
 * errs toward allowing. This rule guards a workspace-wide, all-members-visible action, so
 * erring toward refusing costs one admin a 403 they can report, while erring toward
 * allowing costs every member their net-worth history.
 *
 * That trade is only safe because the union above covers BOTH role families. A
 * steward-only allowlist here would refuse the production workspace owner outright — the
 * exact failure the read-only rule's comment describes.
 */
export function isAdminRoleSet(roleIds: readonly string[]): boolean {
  return roleIds.includes(STEWARD_ROLE_IDS.ADMIN)
    || roleIds.includes(PLATFORM_ADMIN_ROLE_IDS.WORKSPACE_ADMIN)
    || roleIds.includes(PLATFORM_ADMIN_ROLE_IDS.TENANT_ADMIN);
}

/**
 * True when *every* role on the membership is a known role that confers no `inst:write`
 * ({@link NO_WRITE_ROLE_IDS}).
 *
 * The NAME still says "read only" and the list it reads no longer does (steward-app#1102):
 * `FIRM_AUTHORITY` is listed, and `engagement:activate` is a write of a row in appcore's
 * engagement store. The predicate is the narrower one the constant documents — no
 * `inst:write` — and callers must not read a `true` here as "this member changes nothing
 * anywhere". Renaming it is deferred so the constant's rename stays revertable alone.
 *
 * Deliberately **fails open** in two cases, and the reasoning survives the promotion from
 * UX gate to security gate:
 *
 * - An *unrecognised* role id (a future platform role, a custom workspace role) yields
 *   false. Permissions union across roles, so an unknown role may well carry writes, and
 *   denying a legitimate writer is a worse failure than the bounded spend the façade gate
 *   protects. Production bears this out: the family workspace's writing members hold
 *   appcore platform roles and no steward-* role at all, so an allowlist of steward
 *   admin/member would have refused the workspace owner.
 * - An *empty* role list is anomalous rather than proof of read-only. Such a membership
 *   has no `inst:read` either, so appcore refuses the caller's read before any façade
 *   gate is reached.
 */
export function isReadOnlyRoleSet(roleIds: readonly string[]): boolean {
  if (roleIds.length === 0) return false;
  return roleIds.every(id => NO_WRITE_ROLE_IDS.includes(id));
}

/**
 * ── The invite allowlist: the ONLY role ids the façade will pin to an invite ──────────
 *
 * **Reasoning of record: `docs/33-relationship-authorization.md` §11.** Read it before
 * adding or removing an id here. crossmarc-ops holds a DIFFERENT list for the operator
 * plane (`core/src/firm-members.ts`, `OPERATOR_INVITABLE_ROLE_IDS`) and §11 is the single
 * place the two are argued against each other — neither repo re-argues it locally, which
 * is what stops two lists in two repos from drifting into two different answers.
 *
 * That operator constant landed as crossmarc-ops#149 (read on its `main`, 2026-09-10). A
 * separate provisioning-only list for the firm-creation admin invite is still to ship there
 * (crossmarc-ops#147); §11 says what it carries. Check either by grepping crossmarc-ops for
 * the symbol, never by the state of an issue.
 *
 * Enforced by `services/steward-app-api/src/handlers/invites.ts` on `POST /invites`.
 *
 * ⚠️ **That is the only INVITE route that pins a caller-supplied role set. It is NOT the only
 * ROUTE that does, and an earlier draft of this sentence said it was.** `POST /members` and
 * `PUT /members/{userId}` (`handlers/members.ts:7-15`, `router.ts:274-275`) are both bare
 * `roleIds: z.array(z.string()).min(1)` and this list does not reach either — tracked as
 * steward-app#1376, which also records why applying THIS list to `PUT /members` would be
 * wrong (a whole-array REPLACE must restate carried platform roles). Anyone asking "are the
 * member routes covered?" must not find a load-bearing "only" here and stop.
 *
 * (`POST /advisor/connections` also mints invites, but its roleIds come from
 * `advisorRoleIds()` server-side and its input is a `z.strictObject` with no `roleIds` key,
 * so they are not caller-supplied. They are deliberately NOT on this list — §11 item 4.)
 *
 * 🔒 **What this replaces is nothing.** Before 2026-09-10 the route's whole input schema was
 * `roleIds: z.array(z.string()).min(1)` and the SPA's `ROLE_OPTIONS` (`src/api/types.ts`) was
 * a client-side picker. The server-side bound people believed in was appcore's
 * `assertAssignableRoles`, and §11 records what that function actually refuses, with line
 * numbers: a non-`workspace` scope, and a role carrying `*:*` assigned by a caller without
 * one. It has NO allowlist of which workspace-scoped roles. In particular appcore's four
 * built-in `scope: 'workspace'` roles are pre-seeded into its role cache at
 * `expiresAt: Infinity`, so they resolve with no pack mounted and no DynamoDB row —
 * `workspace-admin` among them. ⚠️ It is **not** "a bigger `STEWARD_ROLE_IDS.ADMIN`" — an
 * earlier draft of this paragraph said `strictly larger` and that is false. The two are
 * INCOMPARABLE: `workspace-admin` carries eight platform capabilities the steward role has
 * never held (`aset:*`, `cfg:read`, `cfg:write`, `grant:read`, `grant:manage`, `link:read`,
 * `link:write`, `sched:*`) and lacks every string the steward pack grants its family roles
 * (`assistant:read`, `steward:write`, and every `PILLAR_CAPABILITIES` member — stated as the
 * derived constant rather than a count, which has now moved twice).
 *
 * ⚠️ Deliberately "grants", not "armed strings", which is what this line said until
 * steward-app#1260's review caught it: `PILLAR_CAPABILITIES` now includes `people:read`,
 * which is granted and NOT armed, so "the armed strings" was false about one member of the
 * very set it named. The load-bearing half is unchanged — `workspace-admin` holds none of
 * them either way — but a sweep that corrects counts is exactly where a new false claim gets
 * introduced. Checked by diffing `packRoles.ts`
 * against appcore `packages/core/src/entities/role.ts` on 2026-09-10. What matters here is
 * not which is bigger but that no screen in this SPA has any vocabulary for it. appcore
 * PERMITS the grant deliberately (`auth/permissions.ts` ~305-325 — appcore#603, "admins mint
 * admins", naming `workspace-admin` and the acquired strings), so this list is steward-app
 * declining to OFFER what appcore allows, not a claim appcore is wrong.
 * That is why `workspace-admin` is the discriminating test
 * case for this list: appcore would ACCEPT it. A test that refuses `TENANT_ADMIN` instead
 * proves nothing, because appcore's scope rule already refuses that.
 *
 * ⚠️ **Derived from the id tables above, never hand-typed.** A literal that drifts from
 * `STEWARD_ROLE_IDS` / `STEWARD_ADVISOR_FIRM_ROLE_IDS` is a silent hole: the constant moves,
 * the allowlist keeps the old string, and the role it was meant to admit starts 403ing while
 * the string it now names admits nothing.
 *
 * ⚠️ **NOT the same list as {@link FAMILY_ROLE_IDS}, and do not "simplify" one into the
 * other.** That one is an honesty check over roles a membership already HOLDS (its own
 * docblock says "not an authorization one"), it deliberately admits appcore platform roles
 * because production memberships carry them, and it admits `TENANT_ADMIN`, which appcore
 * refuses on an invite outright. This one is an authorization gate over roles a caller is
 * ASKING FOR, and it is deliberately smaller.
 *
 * The seven ids, and why each is here (§11 has the table and the argument):
 *
 *  - `ADMIN` / `MEMBER` / `VIEWER` — the three the SPA's `ROLE_OPTIONS` has always offered.
 *  - `FIRM_STAFF` — a firm's advisor, on a firm workspace. This, `FIRM_AUTHORITY`,
 *    `FIRM_ROSTER` and `FIRM_PROVISION` are sent by "Your firm"'s Add a person
 *    (`src/advisor/YourFirm.tsx`, steward-app#1361), whose suite asserts the form offers
 *    exactly this list.
 *  - `FIRM_PROVISION` — `engagement:provision`, setting up a sponsored client account
 *    (steward-app#1481). Founder decision 3 on steward-app#1016 (2026-09-16): any firm admin or
 *    advisor may, so every invite that makes a firm person carries it beside `FIRM_STAFF`, and
 *    the firm's own admin can take it back from the People rows on the Firm page (Settings >
 *    Members before steward-app#1729, and still for an admin that page is not offered to). It confers no vault access,
 *    and appcore resolves it only from the caller's membership in the firm record's own
 *    workspace, so on any other membership it grants nothing.
 *  - `FIRM_ROSTER` — `firmroster:write`, the firm's own public advisor roster
 *    (crossmarc/appcore#645). On this list for the same reason as `FIRM_AUTHORITY` and by the
 *    same ruling's logic: a firm admin's invite carries it, so the firm's own admin keeps the
 *    firm's listing true, and can take the role back. It confers no vault access and no
 *    engagement capability; appcore additionally requires the call to be made in the firm's
 *    own workspace, and every roster create to name an invite-accepted member.
 *  - `FIRM_AUTHORITY` — **on this list by founder ruling, 2026-09-09 (steward-app#1343
 *    ruling 2): a firm admin's invite carries firm authority, and the firm's own admin
 *    decides who may accept clients and can take it back.** It is here because of that
 *    ruling and not by inheritance from any earlier appcore behaviour. crossmarc-ops'
 *    operator list deliberately does NOT carry it; §11 item 2 says why the same id gets
 *    opposite answers on the two planes.
 *
 * The list is FLAT rather than split by workspace kind. The four firm ids only resolve in a
 * firm workspace (appcore's pack-mount rule), and the façade cannot tell a firm workspace
 * from a family one without a second round trip. §11 states that trade and its residual —
 * appcore's role cache is keyed by roleId alone with a 60s TTL, so the mount rule is
 * reliable cold and a coin flip warm.
 */
export const INVITABLE_ROLE_IDS: readonly string[] = Object.freeze([
  STEWARD_ROLE_IDS.ADMIN,
  STEWARD_ROLE_IDS.MEMBER,
  STEWARD_ROLE_IDS.VIEWER,
  STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_STAFF,
  STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_AUTHORITY,
  STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_ROSTER,
  STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_PROVISION,
]);

/**
 * The role ids from a requested set that {@link INVITABLE_ROLE_IDS} does not admit, in the
 * order the caller sent them, de-duplicated.
 *
 * Returns the REFUSED ids rather than a boolean so the caller can name them in the error.
 * The caller submitted them, so echoing them back discloses nothing it did not already
 * know — the same argument appcore makes for `ROLE_NOT_ASSIGNABLE`'s message.
 *
 * 🔒 **A caller must REFUSE on a non-empty result, never filter.** Dropping the off-list ids
 * and inviting with the rest mints an invite that grants less than the caller asked for; the
 * caller sees a 201 and finds out when the invitee cannot do their job. That is a different
 * and worse bug than a 403. `docs/33` §11 "The property".
 */
export function rolesNotInvitable(roleIds: readonly string[]): string[] {
  const refused = roleIds.filter(id => !INVITABLE_ROLE_IDS.includes(id));
  return [...new Set(refused)];
}

/**
 * The role ids of a STORED invite that the resend route will not re-issue
 * (`POST /invites/{token}/resend`, steward-app#1462): {@link rolesNotInvitable} on the set less
 * `steward-cash-flow-totals` (steward-app#2591). `POST /invites` adds totals to the role set it
 * stores, AFTER its allowlist, while the cash flow agreement is on, so such an invite is one the
 * product issued and must stay resendable. Totals only, never `steward-cash-flow-every`: no route
 * mints detail onto an invite. A set holding totals and nothing else is judged whole (refused),
 * since `POST /invites` never writes one. The façade's resend and the SPA's Resend button must
 * judge through this one function, so the button is never offered for a resend that can only 409.
 */
export function rolesNotResendable(roleIds: readonly string[]): string[] {
  const rest = roleIds.filter(id => id !== CASH_FLOW_ROLE_IDS.FAMILY_TOTALS);
  return rolesNotInvitable(rest.length > 0 ? rest : roleIds);
}

/**
 * May a membership with these roles hold `steward-firm-provision` (steward-app#1481)? Only when
 * it carries an ADMIN role ({@link isAdminRoleSet}) or `steward-member`.
 *
 * Founder decision 3 on steward-app#1016 is "any firm ADMIN or ADVISOR", and the coordinator's
 * ruling on 2026-09-16 applied it: a read-only Viewer in a firm workspace must not hold a
 * capability that mints a client's tenant and sends the family an invitation. `steward-viewer`
 * alone, and a set with no ordinary role at all, both answer false. Pinned in
 * `inviteRoleAllowlist.test.ts`.
 */
export function mayHoldFirmProvision(roleIds: readonly string[]): boolean {
  return isAdminRoleSet(roleIds) || roleIds.includes(STEWARD_ROLE_IDS.MEMBER);
}

/**
 * True when a requested role set carries `steward-firm-provision` WITHOUT a role that may hold it
 * ({@link mayHoldFirmProvision}). The façade REFUSES such a set on `POST /invites`, `POST /invites/{token}/resend` (the stored roles),
 * `POST /members` and `PUT /members/{userId}` (403, never a filter, for {@link rolesNotInvitable}'s
 * reason). The SPA never sends one: "Your firm" leaves the role off a Viewer invite, the role
 * picker strips it on a change to Viewer, and the Members checkbox is disabled for a Viewer.
 *
 * ⚠️ **A bound on the steward-app browser plane only.** appcore does not know the rule, so
 * crossmarc-ops (which calls appcore's `/admin` directly) and appcore's own seed scripts are not
 * covered by it; neither offers a Viewer set as of 2026-09-16.
 */
export function firmProvisionWithoutBaseRole(roleIds: readonly string[]): boolean {
  return roleIds.includes(STEWARD_ADVISOR_FIRM_ROLE_IDS.FIRM_PROVISION) && !mayHoldFirmProvision(roleIds);
}
